import { migrationSupabase } from '../migrationSupabaseClient';
import { plotMigrationRuntime } from '../config/plotMigrationAccess';

export class PlotMigrationSafetyError extends Error {
  constructor(message, code = 'PLOT_MIGRATION_SAFETY_LOCK') {
    super(message);
    this.name = 'PlotMigrationSafetyError';
    this.code = code;
  }
}
function assertReadsEnabled() {
  if (!plotMigrationRuntime.readsEnabled) {
    throw new PlotMigrationSafetyError(
      'A estrutura territorial está bloqueada até a conexão única de produção ser confirmada.',
      'PLOT_MIGRATION_READS_DISABLED'
    );
  }
}

function assertWritesEnabled() {
  if (!plotMigrationRuntime.writesEnabled) {
    throw new PlotMigrationSafetyError(
      'O envio da revisão está desligado. Nenhuma proposta foi gravada.',
      'PLOT_MIGRATION_WRITES_DISABLED'
    );
  }
}

function unwrapRpc(result, fallbackMessage) {
  if (result.error) {
    throw new PlotMigrationSafetyError(
      result.error.message || fallbackMessage,
      result.error.code || 'PLOT_MIGRATION_RPC_ERROR'
    );
  }
  return result.data;
}

export async function loadOwnedPlotMigrationReview(client = migrationSupabase) {
  assertReadsEnabled();
  const result = await client.rpc('obter_revisao_migracao_talhoes_usuario');
  return unwrapRpc(result, 'Não foi possível carregar a revisão dos seus talhões.');
}

export async function hasOwnedPlotMigrationPending(client = migrationSupabase) {
  assertReadsEnabled();
  const result = await client.rpc('tem_migracao_talhoes_pendente_usuario');
  return unwrapRpc(result, 'Não foi possível conferir se existem talhões pendentes.');
}

export async function effectuateOwnedPlotMigration({
  legacyPlotId,
  plotName,
  decision,
  destinationPhysicalPlotId = null,
  geometry = null,
  calculatedArea = null,
  observation = null,
}, client = migrationSupabase) {
  assertWritesEnabled();
  const result = await client.rpc('efetivar_configuracao_migracao_talhao_usuario', {
    p_talhao_legado_id: legacyPlotId,
    p_nome_talhao: plotName,
    p_decisao: decision,
    p_talhao_fisico_destino_id: destinationPhysicalPlotId,
    p_geometria_proposta: geometry,
    p_area_calculada: calculatedArea,
    p_observacao: observation,
  });
  return unwrapRpc(result, 'Não foi possível efetivar a configuração deste talhão.');
}

export async function saveOwnedPlotMigrationDraft({
  legacyPlotId,
  decision,
  destinationPhysicalPlotId = null,
  geometry = null,
  calculatedArea = null,
  justification = null,
}, client = migrationSupabase) {
  assertWritesEnabled();
  const result = await client.rpc('salvar_revisao_migracao_talhao_usuario', {
    p_talhao_legado_id: legacyPlotId,
    p_decisao: decision,
    p_talhao_fisico_destino_id: destinationPhysicalPlotId,
    p_geometria_proposta: geometry,
    p_area_calculada: calculatedArea,
    p_justificativa: justification,
  });
  return unwrapRpc(result, 'Não foi possível salvar esta proposta de revisão.');
}

export async function submitOwnedPropertyMigrationReview(propertyId, client = migrationSupabase) {
  assertWritesEnabled();
  const result = await client.rpc('enviar_revisao_migracao_propriedade_usuario', {
    p_propriedade_id: propertyId,
  });
  return unwrapRpc(result, 'Não foi possível enviar a propriedade para conferência.');
}

export async function loadPlotMigrationReviewerAccess(client = migrationSupabase) {
  assertReadsEnabled();
  const result = await client.rpc('obter_acesso_revisor_migracao');
  return unwrapRpc(result, 'Não foi possível conferir o acesso à revisão interna.');
}

export async function loadInternalPlotMigrationReview(client = migrationSupabase) {
  assertReadsEnabled();
  const result = await client.rpc('obter_revisao_migracao_talhoes_interna');
  return unwrapRpc(result, 'Não foi possível carregar a revisão interna dos talhões.');
}

export async function loadOperationalPropertyStructure(clients) {
  const client = clients?.migrationClient || migrationSupabase;
  const result = await client.rpc('obter_estrutura_operacional_propriedades_usuario');
  const operational = unwrapRpc(result, 'Não foi possível carregar a estrutura nova das propriedades.');

  if (operational?.estruturaLegadaAlterada !== false) {
    throw new PlotMigrationSafetyError(
      'A estrutura nova das propriedades foi bloqueada pelas verificações de segurança.',
      'PLOT_OPERATIONAL_PROPERTIES_SAFETY'
    );
  }
  return operational;
}

export async function archiveOperationalProperty(propertyId, clients) {
  if (!propertyId) {
    throw new PlotMigrationSafetyError(
      'Selecione uma propriedade válida.',
      'PLOT_OPERATIONAL_PROPERTY_REQUIRED'
    );
  }

  const client = clients?.migrationClient || migrationSupabase;
  const rpcResult = await client.rpc('arquivar_propriedade_operacional_usuario', {
    p_propriedade_id: propertyId,
  });
  const result = unwrapRpc(rpcResult, 'Não foi possível remover a propriedade da estrutura nova.');

  if (result?.arquivada !== true
      || result?.estrutura_legada_alterada !== false
      || result?.historico_preservado !== true) {
    throw new PlotMigrationSafetyError(
      'A remoção foi bloqueada porque o banco não confirmou a preservação do histórico.',
      'PLOT_OPERATIONAL_PROPERTY_ARCHIVE_SAFETY'
    );
  }
  return result;
}

function assertOperationalDeletionResult(result, kind) {
  if (result?.excluido !== true
      || result?.estrutura_legada_alterada !== false
      || result?.historico_preservado !== true) {
    throw new PlotMigrationSafetyError(
      `A exclusão do ${kind} foi bloqueada porque o banco não confirmou a preservação do histórico.`,
      'PLOT_OPERATIONAL_DELETE_SAFETY'
    );
  }
  return result;
}

export async function deleteOperationalPlanting(plantingId, propertyId, clients) {
  if (!plantingId || !propertyId) {
    throw new PlotMigrationSafetyError('Selecione um plantio válido.', 'PLOT_OPERATIONAL_PLANTING_REQUIRED');
  }

  const client = clients?.migrationClient || migrationSupabase;
  const rpcResult = await client.rpc('excluir_plantio_operacional_usuario', {
    p_plantio_id: plantingId,
  });
  const result = unwrapRpc(rpcResult, 'Não foi possível excluir o plantio.');

  return assertOperationalDeletionResult(result, 'plantio');
}

export async function deleteOperationalPlot(plotId, propertyId, clients) {
  if (!plotId || !propertyId) {
    throw new PlotMigrationSafetyError('Selecione um talhão válido.', 'PLOT_OPERATIONAL_PLOT_REQUIRED');
  }

  const client = clients?.migrationClient || migrationSupabase;
  const rpcResult = await client.rpc('excluir_talhao_operacional_usuario', {
    p_talhao_fisico_id: plotId,
  });
  const result = unwrapRpc(rpcResult, 'Não foi possível excluir o talhão.');

  return assertOperationalDeletionResult(result, 'talhão');
}

export async function updateOperationalPlot({
  plotId,
  propertyId,
  name = null,
  geometry = null,
  area = null,
  coordinateX = null,
  coordinateY = null,
}, clients) {
  if (!plotId || !propertyId) {
    throw new PlotMigrationSafetyError(
      'Selecione um talhão válido.',
      'PLOT_OPERATIONAL_UPDATE_REQUIRED'
    );
  }
  if (name == null && geometry == null) {
    throw new PlotMigrationSafetyError(
      'Informe o nome ou o desenho que deve ser atualizado.',
      'PLOT_OPERATIONAL_UPDATE_EMPTY'
    );
  }

  const client = clients?.migrationClient || migrationSupabase;
  const rpcResult = await client.rpc('atualizar_talhao_operacional_usuario', {
    p_talhao_fisico_id: plotId,
    p_propriedade_id: propertyId,
    p_nome: name,
    p_geometria: geometry,
    p_area: area,
    p_coordenada_x: coordinateX,
    p_coordenada_y: coordinateY,
  });
  const result = unwrapRpc(rpcResult, 'Não foi possível atualizar o talhão.');

  if (result?.atualizado !== true
      || String(result?.talhao_fisico_id) !== String(plotId)
      || String(result?.propriedade_id) !== String(propertyId)
      || result?.estrutura_legada_alterada !== false
      || result?.historico_preservado !== true) {
    throw new PlotMigrationSafetyError(
      'A atualização foi bloqueada porque o banco não confirmou a preservação do histórico.',
      'PLOT_OPERATIONAL_UPDATE_SAFETY'
    );
  }
  return result;
}
